import { useEffect, useState } from 'react';
import { useAuth } from './lib/useAuth';
import { AlertWithId, ChannelWithId, acknowledgeAlert, useActionableAlerts, useAlerts, useChannels } from './lib/data';
import { StatusBoard } from './views/StatusBoard';
import { ChannelDetail } from './views/ChannelDetail';
import { AuditLog } from './views/AuditLog';
import { AlertingSettings } from './views/AlertingSettings';
import { AlertsView } from './views/AlertsView';

/** A slowly-ticking clock so staleness / relative times refresh without a reload. */
function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function SignIn({ onSignIn, error }: { onSignIn: () => void; error: string | null }) {
  return (
    <div className="signin">
      <h1>Agent Uptime Monitor</h1>
      <p>Internal access only. Sign in with your Rocketable Google account.</p>
      <button onClick={onSignIn}>Sign in with Google</button>
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}

export function App() {
  const { user, loading, error, signIn, signOutUser } = useAuth();
  // Subscribe only once signed in — an unauthenticated listen is rejected by
  // the rules and its error kills the listener for good (see useChannels).
  const { channels, loading: channelsLoading } = useChannels(!!user);
  const { alerts, loading: alertsLoading } = useAlerts(!!user);
  const actionableAlerts = useActionableAlerts(!!user);
  const now = useNow();
  const [tab, setTab] = useState<'board' | 'alerts' | 'alerting' | 'audit'>('board');
  const [selected, setSelected] = useState<ChannelWithId | null>(null);

  if (loading) return <div className="loading">Loading…</div>;
  if (!user) return <SignIn onSignIn={signIn} error={error} />;

  // Keep the selected channel in sync with live data.
  const selectedLive = selected ? channels.find((c) => c.id === selected.id) ?? null : null;

  // Best-effort channel display name for an alert; falls back to the raw
  // channelId when no live channel matches (alerts outlive channels via TTL).
  const nameFor = (a: AlertWithId) =>
    channels.find((c) => c.productId === a.productId && c.id === a.channelId)?.displayName ?? a.channelId;

  return (
    <div className="app">
      <header className="app-header">
        <h1>Agent Uptime Monitor</h1>
        <nav>
          <button className={tab === 'board' ? 'active' : ''} onClick={() => { setTab('board'); setSelected(null); }}>Status</button>
          <button className={tab === 'alerts' ? 'active' : ''} onClick={() => { setTab('alerts'); setSelected(null); }}>Alerts</button>
          <button className={tab === 'alerting' ? 'active' : ''} onClick={() => { setTab('alerting'); setSelected(null); }}>Alerting</button>
          <button className={tab === 'audit' ? 'active' : ''} onClick={() => { setTab('audit'); setSelected(null); }}>Audit log</button>
        </nav>
        <div className="user">
          <span>{user.email}</span>
          <button onClick={signOutUser}>Sign out</button>
        </div>
      </header>
      <main>
        {tab === 'audit' ? (
          <AuditLog />
        ) : tab === 'alerts' ? (
          <AlertsView alerts={alerts} actionable={actionableAlerts} loading={alertsLoading} nowMs={now} nameFor={nameFor} onAcknowledge={acknowledgeAlert} />
        ) : tab === 'alerting' ? (
          <AlertingSettings />
        ) : selectedLive ? (
          <ChannelDetail channel={selectedLive} nowMs={now} onBack={() => setSelected(null)} />
        ) : (
          <StatusBoard channels={channels} nowMs={now} onOpen={setSelected} loading={channelsLoading} />
        )}
      </main>
    </div>
  );
}
